/**
 * Utility functions for calculating energy (kcal) from macronutrients
 * Uses standard multiplying factors:
 * - Protein: 4 kcal/g
 * - Carbohydrates: 4 kcal/g
 * - Fiber: 2 kcal/g
 * - Fat: 9 kcal/g
 */

const MACRO_CONFIG = [
  {
    id: "protein",
    nutrientNames: ["protein", "proteiner"],
    caloriesPerGram: 4,
  },
  {
    id: "carbs",
    nutrientNames: ["kolhydrater", "carbohydrates", "carb", "kolhydrat"],
    caloriesPerGram: 4,
  },
  {
    id: "fiber",
    nutrientNames: ["fibrer", "fiber", "kostfiber", "kostfibrer"],
    caloriesPerGram: 2,
  },
  {
    id: "fat",
    nutrientNames: ["fett", "fat"],
    caloriesPerGram: 9,
  },
];

/**
 * Find a nutrient by name (case-insensitive)
 */
const findNutrient = (nutrients, names) => {
  if (!Array.isArray(nutrients)) return null;
  const lowerNames = names.map((name) => name.toLowerCase());
  return nutrients.find((nutrient) => {
    const name = (nutrient?.name || "").toString().trim().toLowerCase();
    return lowerNames.includes(name);
  });
};

/**
 * Parse a numeric value, returning 0 if invalid
 */
const parseAmount = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }
  const numeric = parseFloat(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

/**
 * Calculate energy (kcal) from final macro values using multiplying factors
 * @param {Array} nutrients - Array of nutrient objects
 * @param {boolean} perPortion - If true, calculate for portion; if false, calculate per 100g (default)
 * @returns {number} Total energy in kcal
 */
export const calculateEnergyFromMacros = (nutrients, perPortion = false) => {
  if (!Array.isArray(nutrients)) return 0;

  const amountField = perPortion ? "amount_per_portion" : "amount_per_100g";

  let totalEnergy = 0;

  MACRO_CONFIG.forEach((config) => {
    const nutrient = findNutrient(nutrients, config.nutrientNames);
    if (nutrient) {
      const grams = parseAmount(nutrient[amountField] ?? nutrient.amount);
      const energy = grams * config.caloriesPerGram;
      totalEnergy += energy;
    }
  });

  return totalEnergy;
};

/**
 * Get macro values and their calculated energy contributions
 * @param {Array} nutrients - Array of nutrient objects
 * @param {boolean} perPortion - If true, get for portion; if false, get per 100g (default)
 * @returns {Object} Object with macro details and total energy
 */
export const getMacroDetails = (nutrients, perPortion = false) => {
  if (!Array.isArray(nutrients)) {
    return {
      macros: [],
      totalEnergy: 0,
    };
  }

  const amountField = perPortion ? "amount_per_portion" : "amount_per_100g";

  const macroDetails = MACRO_CONFIG.map((config) => {
    const nutrient = findNutrient(nutrients, config.nutrientNames);
    const grams = parseAmount(nutrient?.[amountField] ?? nutrient?.amount);
    const energy = grams * config.caloriesPerGram;

    return {
      ...config,
      grams,
      energy,
    };
  }).filter((macro) => macro.energy > 0);

  const totalEnergy = macroDetails.reduce((sum, macro) => sum + macro.energy, 0);

  return {
    macros: macroDetails,
    totalEnergy,
  };
};







